package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Twelve locale codes on one line, and the page's quietest band by a wide margin.
 *
 * It sits between the hero and the first argument to do one thing: turn "the other eleven" from a
 * number in a headline into twelve things a reader can count. Codes rather than language names,
 * because names in English (`Russian`, `Japanese`) would be the page telling you about languages,
 * where a `ru` and a `ja` are what actually appears in the repository the reader is being asked to
 * push. Twelve of them, so the hero's arithmetic — one written, eleven handled — is checkable.
 *
 * `ru` is the one in the accent. Everything below this strip argues about a Russian plural rule and
 * a Russian date format, so the strip marks its subject before the argument starts rather than
 * introducing it three bands later.
 *
 * The codes carry no `lang` attribute. They are Latin text *naming* languages, not text *in* them:
 * `lang="ru"` on the string "ru" would tell a screen reader to pronounce two Latin letters as
 * Russian, which is both wrong and audible.
 */
@Composable
fun LocaleStrip() {
    Ul(LocaleStripStyle.toModifier().toAttrs { attr("aria-label", tr("marketing.locale_strip.locale_codes", "Locale codes")) }) {
        LOCALE_CODES.forEach { code ->
            Li(
                LocaleCodeStyle.toModifier()
                    .then(if (code == PIVOT_LOCALE) LocalePivotStyle.toModifier() else Modifier)
                    .toAttrs(),
            ) { Text(code) }
        }
    }
}

/**
 * The order is the pen's, not alphabetical and not by speaker count.
 *
 * It opens on the source locale and closes on the two the reader most likely already ships, with
 * the scripts that need their own face scattered through the middle so no run of the strip reads as
 * "the Latin ones, then the hard ones".
 */
private val LOCALE_CODES = listOf(
    "en", "ru", "de", "ja", "ar", "hi", "ko", "el", "pt", "zh", "fr", "es",
)

/** The locale the rest of the page argues about. See [LocalePivotStyle]. */
private const val PIVOT_LOCALE = "ru"

/**
 * The strip: full-bleed, sunken, and sharing every band's gutter ladder.
 *
 * The horizontal padding steps 24 -> 72 -> 92 exactly as `BandStyle` does, so the first and last
 * codes stand in the same column as the headings above and below them. Vertically it is
 * [Space.MD] against the bands' [Space.Gutter] — this is a rule between two arguments, not an
 * argument of its own, and giving it band-sized breathing room would make it one.
 *
 * A grid rather than a flex row, because twelve codes have to survive a phone. The column count
 * only ever takes values that divide twelve, so every wrap produces full rows and the strip never
 * ends on a ragged half-row. Overflow is impossible: the tracks shrink, the codes never spill.
 */
val LocaleStripStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            // The browser's own list margin and inline padding, both of which would break the
            // full-bleed edge this band is made of.
            .margin(0.px)
            .padding(topBottom = Space.MD, leftRight = Space.XL)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .styleModifier {
                property("list-style", "none")
                property("display", "grid")
                property("grid-template-columns", "repeat(4, minmax(0, 1fr))")
                property("column-gap", Space.SM.toString())
                property("align-items", "center")
            }
    }
    Breakpoint.MD {
        Modifier
            .padding(topBottom = Space.MD, leftRight = Space.Hero)
            .styleModifier { property("grid-template-columns", "repeat(6, minmax(0, 1fr))") }
    }
    Breakpoint.LG {
        Modifier
            .padding(topBottom = Space.MD, leftRight = Space.Gutter)
            .maxWidth(Space.PageMax)
            // The tinted rule runs the full width — it is a seam between two arguments — but the
            // twelve codes sit in the page's column with everything else.
            .styleModifier {
                property("margin-inline", "auto")
            }
            .styleModifier { property("grid-template-columns", "repeat(12, minmax(0, 1fr))") }
    }
}

/**
 * One code.
 *
 * The whole cell is the hover target rather than the two glyphs inside it, so a reader running the
 * cursor along the strip gets a continuous response instead of eleven gaps. [transitionColors]
 * carries the default `Motion.FastMs`, which is the 120ms the spec asks of every hover on this
 * page; nothing here moves, resizes, or starts on its own.
 */
val LocaleCodeStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = Space.XS)
            .monoFont()
            .fontSize(TypeScale.Micro)
            .letterSpacing(TypeScale.MicroTracking)
            .textAlign(TextAlign.Center)
            .color(colorMode.toSitePalette().textMuted)
            .transitionColors()
    }
    hover { Modifier.color(colorMode.toSitePalette().accent) }
}

/**
 * `ru`, marked.
 *
 * 700 against the strip's 400, because this is the locale the rest of the page argues about and the
 * accent alone does not carry it in greyscale.
 *
 * This was 500 while JetBrains Mono was requested at `wght@400;500`, where a 700 would have been the
 * browser synthesising a bold — a 10.5px monospace glyph smeared sideways, which reads as a
 * rendering fault rather than as emphasis. The family is now requested across `400..700`, so the
 * weight is drawn rather than faked.
 */
val LocalePivotStyle = CssStyle.base {
    Modifier
        .fontWeight(700)
        .color(colorMode.toSitePalette().accent)
}
